import React, { useEffect, useMemo, useState } from "react";
import { DroppsVariant, variantSelect, productSelect } from "@/store";
import {
  usePdpActions,
  usePdpSellingPlan,
  usePdpProduct,
  usePdpVariant,
} from "./PdpContext";
import { Button } from "@/components/Button";
import { ProductBenefits } from "./ProductBenefits";
import { Checkbox } from "@mui/material";
import { CartAddLineItem, shopifyCartApi } from "@/services/shopifyCart";
import { useCartUiActions } from "../cart";
import { ToggleButton } from "@/components/ToggleButton";
import { Money } from "@/helpers/Money";
import { getFinalAtcCtaPrice } from "@/utils/atcCTAPrice";

export interface AddToCartProps {
  cartSubmitId?: string;
  addToCartText?: string;
  outOfStockText?: string;
  onAddToCartSuccess?: () => void;
}

const DisplayPrice = ({
  price,
  compareAtPrice,
}: {
  price: Money;
  compareAtPrice?: Money;
}) => {
  return (
    <>
      <span className="mr-1 font-semibold">
        <span className="sr-only">Current price</span>
        {price.format()}
      </span>
      {compareAtPrice && compareAtPrice.asCents() > price.asCents() ? (
        <span className="line-through">
          <span className="sr-only">Regular price</span>
          {compareAtPrice.format()}
        </span>
      ) : null}
    </>
  );
};

function getQuantityDiscountMessage(
  quantity_discount: number,
  default_variant_multiplier: number,
  highestMultiplier: number,
  lowestMultiplier: number,
  isBundle: boolean
) {
  if (typeof default_variant_multiplier === "number") {
    if (isBundle) {
      // Render the original message for the highest multiplier
      return (
        <li className="mt-4 font-bold">
          Plus, an{" "}
          <span className="underline">extra {quantity_discount}% OFF</span> for
          bundling up
        </li>
      );
    }

    if (default_variant_multiplier === highestMultiplier) {
      // Render the original message for the highest multiplier
      return (
        <li className="mt-4 font-bold">
          Plus, an{" "}
          <span className="underline">extra {quantity_discount}% OFF</span> on
          our largest size
        </li>
      );
    } else if (default_variant_multiplier === lowestMultiplier) {
      // No message for the lowest multiplier
      // return null;
    } else {
      // Message for the middle case
      return (
        <li className="mt-4 font-bold">
          Plus, an{" "}
          <span className="underline">extra {quantity_discount}% OFF</span> on
          larger box sizes
        </li>
      );
    }
  }

  // Fallback in case default_variant_multiplier is undefined or not a number
}

// Helper function to get the highest multiplier
function getHighestMultiplier(variants: DroppsVariant[]): number {
  return Math.max(
    ...variants.map(
      (variant) => variant.metafields?.default_variant_multiplier || 0
    )
  );
}

// Helper function to get the lowest multiplier
function getLowestMultiplier(variants: DroppsVariant[]): number {
  return Math.min(
    ...variants.map(
      (variant) => variant.metafields?.default_variant_multiplier || 1
    )
  );
}

const OrderTypeButtonGroup = () => {
  const product = usePdpProduct();
  const highestMultiplier = getHighestMultiplier(product.variants);
  const lowestMultiplier = getLowestMultiplier(product.variants);
  const variant = usePdpVariant();
  const actions = usePdpActions();
  const sellingPlan = usePdpSellingPlan();
  const subscription = variantSelect.orderableSubscription(variant);

  const isBulk = product.title.toLocaleLowerCase().includes("bulk");
  const shippingThreshold = 35;

  if (!subscription) {
    return null;
  }
  // Use centralized logic for subscription price and discount
  const subscriptionPrice = variantSelect.lowestPrice(variant, product);
  const subscriptionDiscountCopy = variantSelect.subscriptionDiscountForDisplay(
    variant,
    product
  );
  return (
    <div
      data-id="options-button"
      role="group"
      aria-label="Subscribe and save option"
      className="grid gap-4"
    >
      <div
        className={`group relative rounded border border-solid px-4 py-3 hover:border-primary hover:bg-primary ${
          sellingPlan
            ? "border-primary bg-primary-50 ring-1 ring-primary"
            : "border-gray-600"
        }`}
      >
        <div
          className={`flex ${
            variant?.package_quantity ? "items-start" : ""
          } justify-between`}
        >
          <div className="flex items-center gap-x-2.5">
            <Checkbox
              color="primary"
              name="purchase-type"
              id="product-subscribe"
              onChange={(e) =>
                actions.setSubscriptionPreference(e.target.checked)
              }
              checked={!!sellingPlan}
              title="Subscribe and save"
              className="p-0 group-hover:text-white"
            />
            <label
              htmlFor="product-subscribe"
              className={`mb-0 text-base font-semibold text-gray-600 before:absolute before:-inset-1 before:content-[''] group-hover:text-white ${
                sellingPlan ? "text-primary" : undefined
              }`}
            >
              Subscribe
              {subscriptionDiscountCopy > 0 && (
                <span>{` & Save ${subscriptionDiscountCopy}%`}</span>
              )}
            </label>
          </div>
        </div>

        <ul
          className={`pb-2 pl-2 pt-2 text-[13px] group-hover:text-white lg:text-sm`}
        >
          <li className="list-inside list-disc">Ships every 4 months.</li>
          <li className="list-inside list-disc">
            Change, skip, or cancel at any time
          </li>
        </ul>
      </div>
    </div>
  );
};

// VariantButton component
const VariantButton = ({
  variant,
  selected,
  className,
  labelClassName,
  variantIndex,
  onVariantIndexChange,
}: {
  variant: DroppsVariant;
  selected: boolean;
  className?: string;
  labelClassName?: string;
  variantIndex: number;
  onVariantIndexChange: (index: number) => void;
}) => {
  const actions = usePdpActions();

  return (
    <div
      data-id="variants-button"
      key={variant.id}
      className={`flex-1 ${className}`}
    >
      {variant.quantity_discount !== undefined &&
        (variant.quantity_discount ?? 0) > 0 && (
          <p className={`buy-more-save-more-label ${labelClassName}`}>
            SAVE {variant.quantity_discount ?? 0}%
          </p>
        )}

      <ToggleButton
        className="w-full"
        selected={selected}
        size="md"
        onClick={() => {
          actions.selectVariant(variant);
          onVariantIndexChange(variantIndex);
        }}
        label={
          <span className="text-sm lg:text-base">
            {variantSelect.sizeFormat(variant)}
          </span>
        }
        name="variant-button"
      />
    </div>
  );
};

export const VariantSizeOptionsGroup = ({
  onVariantIndexChange,
}: {
  onVariantIndexChange?: (index: number) => void;
}) => {
  const product = usePdpProduct();
  const selectedVariant = usePdpVariant();
  const defaultVariant = productSelect.defaultVariant(product);
  const visibleVariants = useMemo(
    () => product.variants.filter((v) => !v.hidden),
    [product.variants]
  );

  if (visibleVariants.length === 1) {
    return (
      <p className="body">
        <span className="font-semibold">Size: </span>
        {variantSelect.sizeFormat(defaultVariant)}
      </p>
    );
  } else {
    return (
      <div
        role="radiogroup"
        className="variant-option-buttons flex space-x-2"
        aria-label={`Select product ${defaultVariant.size.unit ?? ""}`}
      >
        {visibleVariants.map((x, index) => (
          <VariantButton
            key={variantSelect.reactKey(x)}
            variant={x}
            selected={x.id == selectedVariant.id}
            className={x.id == selectedVariant.id ? "bg-primary-50" : ""}
            labelClassName={x.id == selectedVariant.id ? "bg-primary-800" : ""}
            variantIndex={index}
            onVariantIndexChange={onVariantIndexChange || (() => {})}
          />
        ))}
      </div>
    );
  }
};

export const AddToCart = ({
  cartSubmitId,
  addToCartText = "Add To Cart",
  outOfStockText = "Out of Stock",
}: AddToCartProps) => {
  const shopifyCartAdder = shopifyCartApi.useTrackedAdd();
  const cartActions = useCartUiActions();
  const variant = usePdpVariant();
  const subscription = variantSelect.orderableSubscription(variant);
  const sellingPlan = usePdpSellingPlan();
  const product = usePdpProduct();
  const onetimeDisplayPrice = variantSelect.onetimePrice(variant, product);
  const subscriptionDisplayPrice = variantSelect.lowestPrice(variant, product);
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
  const isBundle =
    product.bundledItems &&
    product.bundledItems.length > 0 &&
    !product.handle.includes("starter-kit");
  const isTrialProduct = product?.tags?.includes("trial-product");

  // Sync selectedVariantIndex when variant changes externally (e.g., URL params)
  useEffect(() => {
    const currentIndex = product.variants.findIndex((v) => v.id === variant.id);
    if (currentIndex !== -1) {
      setSelectedVariantIndex(currentIndex);
    }
  }, [variant.id, product.variants]);

  // Use the new promo price utility with centralized price logic
  const atcCTAPrice = getFinalAtcCtaPrice({
    productCollections: product.collections,
    basePriceCents: sellingPlan
      ? subscriptionDisplayPrice.asCents()
      : onetimeDisplayPrice.asCents(),
    sellingPlan,
    variant,
    product,
    currency: "$",
    strikeThroughSize: "text-base",
    priceSize: "text-xl",
    strikeThroughColor: "text-primary",
    priceColor: "text-primary",
  });

  const ctaButtonLabel = useMemo(() => {
    if (!variant.available) {
      return outOfStockText;
    }

    if (isTrialProduct) {
      return "Start Your Trial";
    }

    return addToCartText;
  }, [variant.available, addToCartText, outOfStockText, isTrialProduct]);

  const { variantToAdd, quantity } =
    variantSelect.resolveVariantAndQuantityForCartAdd(
      variant,
      product.variants
    );

  // Determine the subscription plan for the variant being added
  const variantToAddSubscription =
    variantSelect.orderableSubscription(variantToAdd);
  const sellingPlanToUse =
    sellingPlan && variantToAddSubscription ? sellingPlan : undefined;

  return (
    <div>
      <VariantSizeOptionsGroup onVariantIndexChange={setSelectedVariantIndex} />
      {!isTrialProduct && (
        <div className="pt-4">
          <OrderTypeButtonGroup />
        </div>
      )}
      <ProductBenefits isTrialProduct={isTrialProduct} />
      <div className="mt-6 basis-full">
        <Button
          id={cartSubmitId}
          className={`group flex w-full bg-green-500 px-4 text-primary not-disabled:hover:bg-green-900 not-disabled:hover:text-white`}
          size="lg"
          disabled={!variant.available}
          onClick={() => {
            const baseLineItem = variantSelect.cartAddLineItem(variantToAdd, {
              subscription: sellingPlanToUse,
              quantity,
              product,
            });

            const lineItem: CartAddLineItem[] = [
              {
                ...baseLineItem,
                properties: baseLineItem.properties ?? undefined,
              },
            ];
            const sourceModule = isBundle
              ? "bundle_pdp_add_to_cart"
              : "pdp_add_to_cart";
            const body = { items: lineItem, sourceModule };
            shopifyCartAdder.mutate(body, {
              onSuccess: () => cartActions.openDrawer(),
            });
          }}
        >
          <div className="flex w-full items-center justify-between">
            <span>{ctaButtonLabel}</span>
            {
              <div className="flex items-center gap-2 group-hover:text-white">
                {product?.type != "Bundle" &&
                  (variant.compareAtPrice &&
                  variant.compareAtPrice.asCents() > variant.price.asCents() ? (
                    <div className="text-sm font-normal tracking-normal text-black group-hover:text-white">
                      <span className="line-through">
                        {variant.compareAtPrice.format()}
                      </span>
                    </div>
                  ) : sellingPlan &&
                    atcCTAPrice.currentPrice != onetimeDisplayPrice.format() ? (
                    <div className="text-sm font-normal tracking-normal text-black group-hover:text-white">
                      <span className="line-through">
                        {onetimeDisplayPrice.format()}
                      </span>
                    </div>
                  ) : null)}
                {product?.type === "Bundle" && variant.compareAtPrice && (
                  <div className="text-sm font-normal tracking-normal text-black group-hover:text-white">
                    <span className="line-through">
                      {variant.compareAtPrice.format()}
                    </span>
                  </div>
                )}
                <span className="text-xl tracking-normal text-black group-hover:text-white">
                  {atcCTAPrice.display}
                </span>
              </div>
            }
          </div>
        </Button>
      </div>
      {!variant.available && (
        <div className="basis-full pt-4">
          <Button
            className="flex w-full"
            aria-label="notify me when restocked"
            size="lg"
            onClick={() => {
              (window as any)._RestockRocket?.openModal();
            }}
          >
            Notify Me When Restocked
          </Button>
        </div>
      )}
    </div>
  );
};
